import { decodeJWT } from '../utils/decodeJWT';
import { AuthStorageService } from './authStorageService';

type AuthExpiredListener = () => void;
type CrossTabIdentityChangeListener = () => void;
type CrossTabLogoutListener = () => void;
type CrossTabConsistentListener = () => void;

/** Cross-tab auth state relative to the identity the meeting was joined with. */
const CROSS_TAB_STATE = {
  consistent: 'consistent',
  loggedOut: 'logged-out',
  identityChange: 'identity-change',
} as const;
type CrossTabState = (typeof CROSS_TAB_STATE)[keyof typeof CROSS_TAB_STATE];

/** How the caller should navigate when a meeting ends, based on the session state at exit. */
export type MeetingExitAction = 'navigate' | 'reload' | 'skip';

/**
 * Tracks whether a meeting is currently active so the auth layer can
 * defer destructive actions (full-page OAuth redirect) until the meeting ends.
 *
 * When an OAuth token refresh fails mid-meeting, the service stores the
 * login callback and fires it automatically once the meeting is torn down.
 *
 * Also watches for a cross-tab auth change during a meeting — a sign-in, an
 * identity switch, or a sign-out — so the tab can notify the user and reload on
 * exit to pick up the new state while keeping the live meeting running.
 */
class MeetingSessionService {
  #active = false;
  #deferredLogin: (() => void) | null = null;
  // The identity the meeting was joined with (null for a guest); the baseline against which
  // cross-tab auth changes are classified.
  #meetingIdentity: string | null = null;
  #diverged = false;
  #authExpiredListeners = new Set<AuthExpiredListener>();
  #crossTabIdentityChangeListeners = new Set<CrossTabIdentityChangeListener>();
  #crossTabLogoutListeners = new Set<CrossTabLogoutListener>();
  #crossTabConsistentListeners = new Set<CrossTabConsistentListener>();
  #observerInstalled = false;
  #idTokenKey = `${AuthStorageService.ROCP_KEY_PREFIX}idToken`;

  isMeetingActive(): boolean {
    return this.#active;
  }

  setMeetingActive(active: boolean): MeetingExitAction {
    this.#active = active;

    if (active) {
      this.#meetingIdentity = this.#identityOf(localStorage.getItem(this.#idTokenKey));
      this.#diverged = false;
      return 'skip';
    }

    if (this.#deferredLogin) {
      console.log('[MeetingSession] Meeting ended with expired auth — triggering deferred login');
      const login = this.#deferredLogin;
      this.#deferredLogin = null;
      this.#resetCrossTabState();
      login();
      return 'skip';
    }

    if (this.#needsReloadOnExit()) {
      console.log(
        '[MeetingSession] Meeting ended after a cross-tab auth change — caller reloads to sync state',
      );
      this.#resetCrossTabState();
      return 'reload';
    }

    return 'navigate';
  }

  /**
   * Called by the auth config when a refresh token expires during an active meeting.
   * Stores the login function so it can be invoked after the meeting ends.
   * Returns `true` if the redirect was deferred (meeting active), `false` otherwise.
   */
  deferLoginIfMeetingActive(login: () => void): boolean {
    if (!this.#active) {
      return false;
    }

    if (this.#deferredLogin) {
      return true;
    }

    console.warn('[MeetingSession] Auth expired during active meeting — deferring re-login');
    this.#deferredLogin = login;
    this.#notifyAuthExpiredListeners();
    return true;
  }

  onAuthExpiredDuringMeeting(listener: AuthExpiredListener): () => void {
    this.#authExpiredListeners.add(listener);
    return () => this.#authExpiredListeners.delete(listener);
  }

  onCrossTabIdentityChange(listener: CrossTabIdentityChangeListener): () => void {
    this.#crossTabIdentityChangeListeners.add(listener);
    return () => this.#crossTabIdentityChangeListeners.delete(listener);
  }

  onCrossTabLogout(listener: CrossTabLogoutListener): () => void {
    this.#crossTabLogoutListeners.add(listener);
    return () => this.#crossTabLogoutListeners.delete(listener);
  }

  onCrossTabConsistent(listener: CrossTabConsistentListener): () => void {
    this.#crossTabConsistentListeners.add(listener);
    return () => this.#crossTabConsistentListeners.delete(listener);
  }

  /** Install the one-time `storage` observer for cross-tab auth changes. */
  observeCrossTabAuthChange(): void {
    if (this.#observerInstalled) {
      return;
    }
    this.#observerInstalled = true;
    window.addEventListener('storage', (event) => this.#handleIdTokenStorageEvent(event));
  }

  #handleIdTokenStorageEvent(event: StorageEvent): void {
    if (!this.#shouldProcessStorageEvent(event)) {
      return;
    }

    const currentIdentity = this.#identityOf(event.newValue);

    switch (this.#classifyState(currentIdentity)) {
      case CROSS_TAB_STATE.consistent:
        this.#handleCrossTabConsistent();
        break;
      case CROSS_TAB_STATE.loggedOut:
        this.#handleCrossTabLogout();
        break;
      case CROSS_TAB_STATE.identityChange:
        this.#handleCrossTabIdentityChange();
        break;
    }
  }

  #shouldProcessStorageEvent(event: StorageEvent): boolean {
    return this.#active && event.key === this.#idTokenKey;
  }

  /** Classify the current cross-tab identity against the one the meeting was joined with. */
  #classifyState(current: string | null): CrossTabState {
    if (current === this.#meetingIdentity) {
      return CROSS_TAB_STATE.consistent;
    }
    if (current === null) {
      return CROSS_TAB_STATE.loggedOut;
    }
    return CROSS_TAB_STATE.identityChange;
  }

  #handleCrossTabConsistent(): void {
    console.info('[MeetingSession] Cross-tab auth back to the meeting identity');
    this.#diverged = false;
    this.#notifyCrossTabConsistentListeners();
  }

  #handleCrossTabLogout(): void {
    console.warn('[MeetingSession] Cross-tab logout during meeting');
    this.#diverged = true;
    this.#notifyCrossTabLogoutListeners();
  }

  #handleCrossTabIdentityChange(): void {
    console.warn('[MeetingSession] Cross-tab identity change during meeting');
    this.#diverged = true;
    this.#notifyCrossTabIdentityChangeListeners();
  }

  #needsReloadOnExit(): boolean {
    return this.#diverged;
  }

  #resetCrossTabState(): void {
    this.#diverged = false;
    this.#meetingIdentity = null;
  }

  #identityOf(rawStoredValue: string | null): string | null {
    if (!rawStoredValue) {
      return null;
    }
    try {
      const { iss, sub, email } = decodeJWT(JSON.parse(rawStoredValue) as string);
      return sub || email ? `${iss}|${sub}|${email}` : null;
    } catch {
      return null;
    }
  }

  #notifyAuthExpiredListeners(): void {
    for (const listener of this.#authExpiredListeners) {
      try {
        listener();
      } catch (e) {
        console.error('[MeetingSession] Auth-expired listener error:', e);
      }
    }
  }

  #notifyCrossTabIdentityChangeListeners(): void {
    for (const listener of this.#crossTabIdentityChangeListeners) {
      try {
        listener();
      } catch (e) {
        console.error('[MeetingSession] Cross-tab identity-change listener error:', e);
      }
    }
  }

  #notifyCrossTabLogoutListeners(): void {
    for (const listener of this.#crossTabLogoutListeners) {
      try {
        listener();
      } catch (e) {
        console.error('[MeetingSession] Cross-tab logout listener error:', e);
      }
    }
  }

  #notifyCrossTabConsistentListeners(): void {
    for (const listener of this.#crossTabConsistentListeners) {
      try {
        listener();
      } catch (e) {
        console.error('[MeetingSession] Cross-tab consistent listener error:', e);
      }
    }
  }
}

export const meetingSessionService = new MeetingSessionService();
